import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaTrash } from "react-icons/fa";
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  Table,
  Image,
} from "react-bootstrap";
import Swal from "sweetalert2";

const Sliders = () => {
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    title: "",
    subtitle: "",
    image_url: null,
    is_active: "1",
  });

  const fetchSliders = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-slider`
      );
      setSliders(res.data.sliders);
    } catch (error) {
      Swal.fire("Error", "Failed to fetch sliders", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setForm({ ...form, [name]: files[0] });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title || !form.subtitle || !form.image_url) {
      Swal.fire("Error", "All fields are required!", "error");
      return;
    }

    const formData = new FormData();
    formData.append("title", form.title);
    formData.append("subtitle", form.subtitle);
    formData.append("image_url", form.image_url);
    formData.append("is_active", form.is_active);

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/add-slider`, formData);
      setForm({
        title: "",
        subtitle: "",
        image_url: null,
        is_active: "1",
      });
      Swal.fire("Success", "Slider added successfully", "success");
      fetchSliders();
    } catch (err) {
      Swal.fire("Error", "Failed to add slider", "error");
    }
  };

  const deleteSlider = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will delete the slider permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/delete-slider/${id}`
        );
        setSliders(sliders.filter((s) => s.id !== id));
        Swal.fire("Deleted!", "Slider deleted successfully.", "success");
      } catch (error) {
        Swal.fire("Error", "Failed to delete slider", "error");
      }
    }
  };

  const toggleSliderStatus = async (id) => {
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/toggle-status/${id}`
      );
      Swal.fire("Updated", res.data.message, "success");
      fetchSliders(); // Refresh list
    } catch (error) {
      Swal.fire("Error", "Failed to update status", "error");
      console.log(error.message);
    }
  };

  useEffect(() => {
    fetchSliders();
  }, []);

  return (
    <Container className="my-5">
      <Row>
        {/* Add New Slider */}
        <Col md={4}>
          <h3 className="text-primary mb-3">Add New Slider</h3>
          <Form
            onSubmit={handleSubmit}
            className=" p-4 rounded shadow-sm"
            style={{ backgroundColor: "aliceblue" }}
          >
            <Form.Group>
              <Form.Label className="fw-bold">Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="Enter title"
                required
              />
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label className="fw-bold">Subtitle</Form.Label>
              <Form.Control
                type="text"
                name="subtitle"
                value={form.subtitle}
                onChange={handleChange}
                placeholder="Enter subtitle"
                required
              />
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label className="fw-bold">Slider Image</Form.Label>
              <Form.Control
                type="file"
                name="image_url"
                onChange={handleChange}
                accept="image/*"
                required
              />
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label className="fw-bold">Status</Form.Label>
              <Form.Select
                name="is_active"
                value={form.is_active}
                onChange={handleChange}
              >
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </Form.Select>
            </Form.Group>

            <Button
              type="submit"
              variant="success"
              className="w-100 mt-4 fw-bold"
            >
              Add Slider
            </Button>
          </Form>
        </Col>

        {/* Sliders Table */}
        <Col md={8}>
          <h3 className="text-primary mb-3">Sliders</h3>
          {loading ? (
            <div className="text-center">Loading...</div>
          ) : (
            <Table
              style={{ backgroundColor: "aliceblue" }}
              bordered
              hover
              responsive
              className="text-center align-middle"
            >
              <thead className="table-primary">
                <tr>
                  <th>#</th>
                  <th>Image</th>
                  <th>Title</th>
                  <th>Subtitle</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {sliders.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-muted">
                      No sliders found.
                    </td>
                  </tr>
                ) : (
                  sliders.map((slider, index) => (
                    <tr key={slider.id}>
                      <td>{index + 1}</td>
                      <td>
                        <Image
                          src={`${process.env.REACT_APP_API_URL}/${slider.image_url}`}
                          alt="slider"
                          width={80}
                          height={60}
                          thumbnail
                        />
                      </td>
                      <td>{slider.title}</td>
                      <td>{slider.subtitle}</td>
                      <td>
                        <span
                          className={`badge ${
                            slider.is_active === 1
                              ? "bg-success"
                              : "bg-secondary"
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => toggleSliderStatus(slider.id)}
                        >
                          {slider.is_active === 1 ? "Active" : "Inactive"}
                        </span>
                      </td>

                      <td>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => deleteSlider(slider.id)}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default Sliders;
